// Strava API integration with OAuth2 support
import { normalizeActivityType } from './utils/activityTypes';

export class StravaAPI {
  // `onTokenUpdate` is fired whenever refreshAccessToken successfully rotates
  // tokens. It receives `{ accessToken, refreshToken, expiresAt }`. The
  // intended use is to keep localStorage / React state / any other places
  // that cache the token in sync with the instance — otherwise a silent
  // internal refresh (e.g. during ensureValidToken inside getRecentWorkouts)
  // leaves localStorage holding stale (now-invalid) tokens.
  //
  // Strava rotates refresh tokens on every refresh, so a stale localStorage
  // refresh token cannot be recovered — any subsequent App.js-driven refresh
  // will 401. This callback is the single mechanism that prevents that
  // poisoning. Always pass it for long-lived instances.
  constructor(accessToken = null, onTokenUpdate = null) {
    this.accessToken = accessToken;
    this.onTokenUpdate = onTokenUpdate;
    // Single-flight guard for token refresh. loadWorkoutData fires several
    // methods in Promise.all (getRecentWorkouts + getAthleteGear + getAthleteZones,
    // then up to 5 getActivityHRZones), each of which calls ensureValidToken. When
    // the token is near expiry they would otherwise trigger concurrent refreshes —
    // and because Strava rotates+invalidates the refresh token on every refresh,
    // the parallel refreshes brick each other's refresh token, corrupt localStorage,
    // and waste rate-limited requests. This promise coalesces them into ONE refresh.
    this._refreshPromise = null;
    // Base URL is env-overridable to ease the June 1 2027 migration to
    // https://www.api-v3.strava.com (Strava API Developer Program changes).
    // Set REACT_APP_STRAVA_API_BASE to switch without a code change.
    this.baseURL = process.env.REACT_APP_STRAVA_API_BASE || 'https://www.strava.com/api/v3';
    this.authURL = 'https://www.strava.com/oauth/authorize';
    this.tokenURL = 'https://www.strava.com/oauth/token';

    // These will be set from environment variables or user input
    this.clientId = process.env.REACT_APP_STRAVA_CLIENT_ID;
    this.clientSecret = process.env.REACT_APP_STRAVA_CLIENT_SECRET;
  }
  // Initialize OAuth2 flow
  initiateOAuth(redirectUri = window.location.origin) {
    // Debug logging to help troubleshoot OAuth issues
    console.log('Initiating Strava OAuth with:', {
      clientId: this.clientId,
      redirectUri: redirectUri,
      hasClientId: !!this.clientId,
      hasClientSecret: !!this.clientSecret
    });
    
    if (!this.clientId) {
      console.error('Strava Client ID is not configured! Check environment variables.');
      alert('Strava integration is not properly configured. Please contact the administrator.');
      return;
    }
    
    const scope = 'read,activity:read_all,profile:read_all';
    const state = this.generateRandomState(); // For security
    
    // Store state in localStorage for verification later
    localStorage.setItem('strava_oauth_state', state);
    
    const authUrl = `${this.authURL}?` + new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: scope,
      state: state
    });
    
    console.log('Redirecting to Strava OAuth URL:', authUrl);
    window.location.href = authUrl;
  }
  // Exchange authorization code for access token
  async exchangeCodeForToken(code, state, redirectUri = window.location.origin) {
    try {
      // Verify state parameter
      const storedState = localStorage.getItem('strava_oauth_state');
      if (state !== storedState) {
        throw new Error('Invalid state parameter - possible CSRF attack');
      }
      
      const response = await fetch(this.tokenURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: this.clientId,
          client_secret: this.clientSecret,
          code: code,
          grant_type: 'authorization_code',
          redirect_uri: redirectUri
        })
      });

      if (!response.ok) {
        throw new Error('Failed to exchange code for token');
      }

      const tokenData = await response.json();
      
      // Store tokens securely
      this.accessToken = tokenData.access_token;
      this.refreshToken = tokenData.refresh_token;
      this.expiresAt = Date.now() + (tokenData.expires_in * 1000);
      
      // Clean up OAuth state
      localStorage.removeItem('strava_oauth_state');
      
      return {
        success: true,
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token,
        expiresAt: this.expiresAt,
        athlete: tokenData.athlete
      };
    } catch (error) {
      console.error('Error exchanging code for token:', error);
      return { success: false, error: error.message };
    }
  }

  // Refresh expired access token
  async refreshAccessToken() {
    try {
      if (!this.refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(this.tokenURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: this.clientId,
          client_secret: this.clientSecret,
          refresh_token: this.refreshToken,
          grant_type: 'refresh_token'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const tokenData = await response.json();

      this.accessToken = tokenData.access_token;
      this.refreshToken = tokenData.refresh_token;
      this.expiresAt = Date.now() + (tokenData.expires_in * 1000);

      // Notify any external cache (localStorage, React state, aiCoach)
      // about the rotated tokens. Without this, an internal refresh
      // triggered by ensureValidToken leaves other token holders stale
      // and they will 401 the next time they call any /api endpoint
      // that validates the token server-side (e.g. /api/coachingData).
      // Callback errors are caught and logged but do not fail the
      // refresh — the instance itself is already updated.
      if (typeof this.onTokenUpdate === 'function') {
        try {
          this.onTokenUpdate({
            accessToken: this.accessToken,
            refreshToken: this.refreshToken,
            expiresAt: this.expiresAt,
          });
        } catch (cbErr) {
          console.error('StravaAPI: onTokenUpdate callback threw:', cbErr);
        }
      }

      return {
        success: true,
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token,
        expiresAt: this.expiresAt
      };
    } catch (error) {
      console.error('Error refreshing token:', error);
      return { success: false, error: error.message };
    }
  }

  // Check if token needs refresh
  async ensureValidToken() {
    if (!this.accessToken) {
      throw new Error('No access token available');
    }

    // If token expires in less than 5 minutes, refresh it — but coalesce
    // concurrent callers onto a SINGLE in-flight refresh (see _refreshPromise).
    // Without this, the Promise.all in loadWorkoutData triggers 3+ simultaneous
    // refreshes; Strava invalidates the prior refresh token on the first one, so
    // the rest fail and brick the token chain. Once the shared refresh resolves,
    // expiresAt is in the future, so later callers skip refresh entirely.
    if (this.expiresAt && Date.now() > (this.expiresAt - 5 * 60 * 1000)) {
      if (!this._refreshPromise) {
        this._refreshPromise = this.refreshAccessToken().finally(() => {
          this._refreshPromise = null;
        });
      }
      const refreshResult = await this._refreshPromise;
      if (!refreshResult.success) {
        throw new Error('Failed to refresh token: ' + refreshResult.error);
      }
    }
  }

  // Get athlete profile
  async getAthleteProfile() {
    try {
      await this.ensureValidToken();
      
      const response = await fetch(`${this.baseURL}/athlete`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch athlete profile');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching athlete profile:', error);
      return null;
    }
  }

  // Get athlete's gear (bikes + shoes) and profile defaults from the athlete endpoint.
  // Returns bikes, shoes, and any available training defaults (ftp, weight, measurementPreference).
  async getAthleteGear() {
    try {
      await this.ensureValidToken();
      const response = await fetch(`${this.baseURL}/athlete`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch athlete gear');
      const data = await response.json();
      return {
        bikes: data.bikes || [],
        shoes: data.shoes || [],
        ftp: data.ftp || null,
        weight: data.weight ? parseFloat(data.weight.toFixed(1)) : null,
        measurementPreference: data.measurement_preference || null,
      };
    } catch (error) {
      console.error('Error fetching athlete gear:', error);
      return { bikes: [], shoes: [] };
    }
  }

  // Get recent activities (workouts)
  //
  // IMPORTANT: When called with `after` (and no `before`), Strava returns
  // activities in ASCENDING order (oldest first), capped at `per_page` items.
  // Without pagination, athletes with > per_page activities in the window get
  // back ONLY their oldest activities — which silently breaks every "recent"
  // view (Dashboard, Report, AI context). We therefore page through all
  // results until either Strava returns < per_page (last page) or we hit
  // `maxPages` as a safety cap.
  async getRecentWorkouts(days = 180, { perPage = 200, maxPages = 10 } = {}) {
    try {
      await this.ensureValidToken();

      const afterTimestamp = Math.floor((Date.now() - (days * 24 * 60 * 60 * 1000)) / 1000);
      const collected = [];

      for (let page = 1; page <= maxPages; page++) {
        const response = await fetch(
          `${this.baseURL}/athlete/activities?after=${afterTimestamp}&per_page=${perPage}&page=${page}`,
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch activities');
        }

        const batch = await response.json();
        if (!Array.isArray(batch) || batch.length === 0) break;

        collected.push(...batch);
        if (batch.length < perPage) break; // last page
      }

      return this.processWorkoutData(collected);
    } catch (error) {
      console.error('Error fetching recent workouts:', error);
      return null;
    }
  }

  // Get detailed activity data
  async getActivityDetail(activityId) {
    try {
      await this.ensureValidToken();

      const response = await fetch(`${this.baseURL}/activities/${activityId}`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch activity detail');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching activity detail:', error);
      return null;
    }
  }

  // Get a processed, detail-rich workout object for a specific activity.
  // Used for explicit deep-analysis flows so regular sync can stay lightweight.
  async getDetailedWorkout(activityId, baseWorkout = null) {
    try {
      const [activity, hrZones] = await Promise.all([
        this.getActivityDetail(activityId),
        this.getActivityHRZones(activityId)
      ]);

      if (!activity) return baseWorkout;

      const detailedWorkout = this.processActivityData(activity, { detailed: true });
      return {
        ...baseWorkout,
        ...detailedWorkout,
        hrZones: hrZones || baseWorkout?.hrZones || null
      };
    } catch (error) {
      console.error('Error building detailed workout:', error);
      return baseWorkout;
    }
  }

  // Get HR zone distribution for a single activity (time in each zone, in seconds)
  async getActivityHRZones(activityId) {
    try {
      await this.ensureValidToken();
      const response = await fetch(`${this.baseURL}/activities/${activityId}/zones`, {
        headers: { 'Authorization': `Bearer ${this.accessToken}` }
      });
      if (!response.ok) return null;
      const zones = await response.json();
      const hrZone = zones.find(z => z.type === 'heartrate');
      if (!hrZone?.distribution_buckets) return null;
      const buckets = hrZone.distribution_buckets;
      return {
        z1: Math.round((buckets[0]?.time || 0) / 60),
        z2: Math.round((buckets[1]?.time || 0) / 60),
        z3: Math.round((buckets[2]?.time || 0) / 60),
        z4: Math.round((buckets[3]?.time || 0) / 60),
        z5: Math.round((buckets[4]?.time || 0) / 60),
      };
    } catch {
      return null;
    }
  }

  // Fetch raw time-series streams for a single activity (time, heartrate, watts,
  // velocity_smooth, altitude by default). Used ONLY on-demand by the Activity
  // Detail card's rolling-average charts — never during regular sync/list views,
  // since streams are one of the more expensive Strava endpoints to call per-row.
  // Returns a plain object keyed by stream type (e.g. { time: [...], heartrate: [...] })
  // or null if unavailable/failed, so callers can degrade to summary-only analysis.
  async getActivityStreams(activityId, keys = ['time', 'heartrate', 'watts', 'velocity_smooth', 'altitude']) {
    try {
      if (activityId === null || activityId === undefined || activityId === '' || !Array.isArray(keys)) return null;
      const requestedKeys = keys
        .filter(key => typeof key === 'string' && key.trim())
        .map(key => key.trim());
      if (requestedKeys.length === 0) return null;

      await this.ensureValidToken();
      const keyParam = requestedKeys.map(encodeURIComponent).join(',');
      const encodedActivityId = encodeURIComponent(String(activityId));
      const response = await fetch(
        `${this.baseURL}/activities/${encodedActivityId}/streams?keys=${keyParam}&key_by_type=true`,
        { headers: { 'Authorization': `Bearer ${this.accessToken}` } }
      );
      if (!response.ok) return null;
      const data = await response.json();
      if (!data || typeof data !== 'object') return null;

      // Strava returns { heartrate: { data: [...], series_type, ... }, ... } —
      // flatten to { heartrate: [...] } for simpler downstream consumption.
      const flattened = {};
      requestedKeys.forEach((key) => {
        if (Array.isArray(data[key]?.data)) flattened[key] = data[key].data;
      });
      return Object.keys(flattened).length > 0 ? flattened : null;
    } catch {
      return null;
    }
  }

  // Fetch athlete-level HR and power zone boundaries from Strava.
  // Returns { heartRate: [{zone, min, max}], power: [{zone, min, max}] } or null on failure.
  async getAthleteZones() {
    try {
      await this.ensureValidToken();
      const response = await fetch(`${this.baseURL}/athlete/zones`, {
        headers: { 'Authorization': `Bearer ${this.accessToken}` }
      });
      if (!response.ok) return null;
      const data = await response.json();

      const mapZones = (rawZones) =>
        (rawZones || [])
          .map((z, i) => ({ zone: i + 1, min: z.min ?? 0, max: z.max ?? -1 }))
          .filter((_, i, arr) => arr.some(z => z.min > 0 || z.max > 0)); // skip all-zero sets

      const heartRate = mapZones(data.heart_rate?.zones);
      const power = mapZones(data.power?.zones);

      if (!heartRate.length && !power.length) return null;
      return { heartRate, power };
    } catch {
      return null;
    }
  }

  // Enrich the most recent N workouts with HR zone data (separate API calls, used sparingly)
  async enrichWithHRZones(workouts, count = 5) {
    const toEnrich = workouts.slice(0, count);
    const enriched = await Promise.all(
      toEnrich.map(async (w) => {
        if (!w.id || !w.averageHeartRate) return w; // skip if no HR data
        const hrZones = await this.getActivityHRZones(w.id);
        return hrZones ? { ...w, hrZones } : w;
      })
    );
    return [...enriched, ...workouts.slice(count)];
  }

  // Process workout data to match app's expected format
  processWorkoutData(activities) {
    return activities.map(activity => this.processActivityData(activity));
  }

  processActivityData(activity, { detailed = false } = {}) {
    const mappedType = this.mapActivityType(activity.sport_type || activity.type);
    const gearId = activity.gear_id || activity.gear?.id || null;
    const movingTime = activity.moving_time || 0;
    const averageSpeed = activity.average_speed || null;
    const maxSpeed = activity.max_speed || null;
    const calories = activity.calories
      ?? (activity.kilojoules ? Math.round(activity.kilojoules * 0.239) : null);

    const workout = {
      id: activity.id,
      name: activity.name,
      date: activity.start_date,
      type: mappedType,
      sportType: activity.sport_type || activity.type || null,
      duration: movingTime, // in seconds
      elapsedTime: activity.elapsed_time || null,
      distance: activity.distance, // in meters
      elevation: activity.total_elevation_gain, // in meters
      elevHigh: activity.elev_high || null,
      elevLow: activity.elev_low || null,
      averageHeartRate: activity.average_heartrate,
      maxHeartRate: activity.max_heartrate,
      averagePower: activity.average_watts,
      maxPower: activity.max_watts,
      weightedAveragePower: activity.weighted_average_watts || null,
      averageSpeed,
      maxSpeed,
      calories,
      kilojoules: activity.kilojoules || null,
      deviceWatts: activity.device_watts ?? null,
      tss: activity.suffer_score, // Strava's Training Stress Score equivalent
      description: activity.description,
      trainer: activity.trainer ?? false,
      commute: activity.commute ?? false,
      manual: activity.manual ?? false,
      private: activity.private ?? false,
      workoutType: activity.workout_type ?? null,
      gear: gearId,
      gearName: activity.gear?.name || null,
      source: 'strava'
    };

    if (!detailed) {
      return workout;
    }

    return {
      ...workout,
      hasDetailedData: true,
      deviceName: activity.device_name || null,
      gearDetail: activity.gear || null,
      photosCount: activity.total_photo_count ?? activity.photo_count ?? null,
      splitsMetric: (activity.splits_metric || []).map(split => ({
        split: split.split,
        distance: split.distance,
        elapsedTime: split.elapsed_time,
        movingTime: split.moving_time,
        averageSpeed: split.average_speed,
        elevationDifference: split.elevation_difference,
        paceZone: split.pace_zone
      })),
      laps: (activity.laps || []).map(lap => ({
        id: lap.id,
        name: lap.name,
        distance: lap.distance,
        elapsedTime: lap.elapsed_time,
        movingTime: lap.moving_time,
        averageSpeed: lap.average_speed,
        maxSpeed: lap.max_speed,
        averageCadence: lap.average_cadence,
        elevationGain: lap.total_elevation_gain,
        startDate: lap.start_date
      })),
      bestEfforts: (activity.best_efforts || []).map(effort => ({
        name: effort.name,
        distance: effort.distance,
        elapsedTime: effort.elapsed_time,
        movingTime: effort.moving_time,
        averageCadence: effort.average_cadence,
        averagePower: effort.average_watts,
        averageHeartRate: effort.average_heartrate,
        maxHeartRate: effort.max_heartrate,
        prRank: effort.pr_rank,
        komRank: effort.kom_rank
      })),
      segmentEfforts: (activity.segment_efforts || []).map(effort => ({
        name: effort.name,
        distance: effort.distance,
        elapsedTime: effort.elapsed_time,
        movingTime: effort.moving_time,
        averageCadence: effort.average_cadence,
        averagePower: effort.average_watts,
        averageHeartRate: effort.average_heartrate,
        maxHeartRate: effort.max_heartrate,
        prRank: effort.pr_rank,
        komRank: effort.kom_rank,
        isKom: effort.is_kom
      }))
    };
  }

  // Map Strava activity types to app's workout types
  mapActivityType(stravaType) {
    return normalizeActivityType(stravaType);
  }

  // Generate random state for OAuth security
  generateRandomState() {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }

  // Test API connection
  async testConnection() {
    try {
      const profile = await this.getAthleteProfile();
      return profile !== null;
    } catch (error) {
      return false;
    }
  }
}
